import type { CreasePattern } from '@/origami/types';

/**
 * Local project storage. IndexedDB with a localStorage fallback, because the
 * whole app is backend-free and a user's work must survive a reload.
 */
const DB_NAME = 'fold-app';
const DB_VERSION = 1;
const STORE = 'projects';
const AUTOSAVE_KEY = '__autosave__';

/**
 * The app was called "Origami Lab" before it became fold.app, and the storage
 * keys carried that name. Anyone who used the old build has projects and an
 * autosave sitting under the old names, so the new build adopts them once on
 * first run rather than silently losing someone's work to a rename.
 */
const LEGACY_DB_NAME = 'origami-lab';
const LEGACY_PREFIX = 'origami-lab:';
const PREFIX = 'fold-app:';
const MIGRATION_FLAG = 'fold-app:migrated-from-origami-lab';

export interface StoredProject {
  id: string;
  name: string;
  updatedAt: number;
  pattern: CreasePattern;
}

function openDb(name = DB_NAME): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null);
      return;
    }
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(name, DB_VERSION);
    } catch {
      resolve(null);
      return;
    }
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>,
  name = DB_NAME,
): Promise<T | null> {
  const db = await openDb(name);
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, mode);
      const request = fn(tx.objectStore(STORE));
      request.onsuccess = () => resolve(request.result as T);
      request.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

export async function saveProject(project: StoredProject): Promise<boolean> {
  const ok = await withStore('readwrite', (store) => store.put(project) as IDBRequest<unknown>);
  if (ok !== null) return true;
  try {
    localStorage.setItem(PREFIX + project.id, JSON.stringify(project));
    return true;
  } catch {
    return false;
  }
}

export async function loadProject(id: string): Promise<StoredProject | null> {
  const result = await withStore<StoredProject>('readonly', (store) => store.get(id));
  if (result) return result;
  try {
    const raw = localStorage.getItem(PREFIX + id) ?? localStorage.getItem(LEGACY_PREFIX + id);
    return raw ? (JSON.parse(raw) as StoredProject) : null;
  } catch {
    return null;
  }
}

export async function listProjects(): Promise<StoredProject[]> {
  const result = await withStore<StoredProject[]>('readonly', (store) => store.getAll());
  const list = result ?? [];
  if (list.length === 0) {
    try {
      const out: StoredProject[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key === MIGRATION_FLAG) continue;
        if (key?.startsWith(PREFIX) || key?.startsWith(LEGACY_PREFIX)) {
          out.push(JSON.parse(localStorage.getItem(key) as string) as StoredProject);
        }
      }
      return out.sort((a, b) => b.updatedAt - a.updatedAt);
    } catch {
      return [];
    }
  }
  return list.filter((p) => p.id !== AUTOSAVE_KEY).sort((a, b) => b.updatedAt - a.updatedAt);
}

export async function deleteProject(id: string): Promise<void> {
  await withStore('readwrite', (store) => store.delete(id) as IDBRequest<undefined>);
  try {
    localStorage.removeItem(PREFIX + id);
    localStorage.removeItem(LEGACY_PREFIX + id);
  } catch {
    /* ignore */
  }
}

export const autosave = (pattern: CreasePattern): Promise<boolean> =>
  saveProject({
    id: AUTOSAVE_KEY,
    name: pattern.meta.title ?? 'Autosave',
    updatedAt: Date.now(),
    pattern,
  });

export const loadAutosave = (): Promise<StoredProject | null> => loadProject(AUTOSAVE_KEY);

/**
 * Copy anything stored under the old "Origami Lab" names into the current ones,
 * once. Runs before the first read at startup; failures are silent because a
 * missing legacy store is the normal case, not an error.
 */
export async function migrateLegacyStorage(): Promise<number> {
  let done = false;
  try {
    done = localStorage.getItem(MIGRATION_FLAG) === '1';
  } catch {
    return 0;
  }
  if (done) return 0;

  let moved = 0;
  const legacy = await withStore<StoredProject[]>(
    'readonly',
    (store) => store.getAll(),
    LEGACY_DB_NAME,
  );
  for (const project of legacy ?? []) {
    if (!project?.id || !project.pattern) continue;
    const existing = await withStore<StoredProject>('readonly', (store) => store.get(project.id));
    if (existing) continue;
    if (await saveProject(project)) moved++;
  }

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith(LEGACY_PREFIX)) continue;
      const target = PREFIX + key.slice(LEGACY_PREFIX.length);
      if (localStorage.getItem(target) === null) {
        localStorage.setItem(target, localStorage.getItem(key) as string);
        moved++;
      }
    }
    localStorage.setItem(MIGRATION_FLAG, '1');
  } catch {
    /* storage unavailable; nothing to migrate */
  }
  return moved;
}
